import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AlertDialog from 'components/UI/AlertDialog';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import React, { useMemo } from 'react';
import {
  useCancelWhatsAppCampaignMutation,
  useGetAllWhatsAppCampaignsQuery,
  type WhatsAppCampaign,
} from 'services/api/adminCampaigns.api';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

interface CampaignsListProps {
  onViewCampaign: (campaignId: number) => void;
  onCreateCampaign: () => void;
}

type StatusFilter = 'ALL' | WhatsAppCampaign['state'];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'ALL', label: 'Todos' },
  { value: 'RUNNING', label: 'En Ejecución' },
  { value: 'COMPLETED', label: 'Completada' },
  { value: 'CANCELLED', label: 'Cancelada' },
];

const CampaignsList: React.FC<CampaignsListProps> = ({ onViewCampaign, onCreateCampaign }) => {
  const { selectedBusinessUnitId } = useSelectedBusinessUnit();
  const { data: campaignsData, isLoading, error, refetch } = useGetAllWhatsAppCampaignsQuery(undefined);
  const [cancelCampaign] = useCancelWhatsAppCampaignMutation();
  const [cancelDialog, setCancelDialog] = React.useState<{ open: boolean; campaign: WhatsAppCampaign | null }>({
    open: false,
    campaign: null,
  });
  const [nameFilter, setNameFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('ALL');

  const handleCancelCampaign = async () => {
    if (!cancelDialog.campaign) return;
    try {
      await cancelCampaign(cancelDialog.campaign.id).unwrap();
      setCancelDialog({ open: false, campaign: null });
      await refetch();
    } catch (e) {
      console.error('Error cancelling campaign:', e);
    }
  };

  const navigateToLeadsWithCampaignFilter = (campaignName: string, businessUnitId: number | null) => {
    const filterModel = {
      items: [
        { field: 'whatsAppCampaign', operator: 'in', value: campaignName },
        { field: 'discarded', operator: 'equals', value: false },
      ],
    };
    const params = new URLSearchParams();
    params.set('viewMode', 'table');
    if (businessUnitId) params.set('businessUnitId', String(businessUnitId));
    params.set('filterModel', JSON.stringify(filterModel));
    window.open(`/leads?${params.toString()}`, '_blank');
  };

  const getStateChip = (state: WhatsAppCampaign['state']) => {
    switch (state) {
      case 'RUNNING':
        return (
          <Chip
            icon={<PlayCircleIcon sx={{ fontSize: '14px !important' }} />}
            label="En Ejecución"
            size="small"
            sx={{
              bgcolor: 'rgba(37, 97, 111, 0.12)',
              color: '#25616F',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              borderRadius: RADIUS_ITEM,
              '& .MuiChip-icon': { color: '#25616F' },
            }}
          />
        );
      case 'COMPLETED':
        return (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
            label="Completada"
            size="small"
            sx={{
              bgcolor: 'rgba(21, 24, 58, 0.08)',
              color: NAVY,
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              borderRadius: RADIUS_ITEM,
              '& .MuiChip-icon': { color: NAVY },
            }}
          />
        );
      case 'CANCELLED':
        return (
          <Chip
            icon={<CancelIcon sx={{ fontSize: '14px !important' }} />}
            label="Cancelada"
            size="small"
            sx={{
              bgcolor: 'rgba(176, 0, 32, 0.1)',
              color: '#B00020',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              borderRadius: RADIUS_ITEM,
              '& .MuiChip-icon': { color: '#B00020' },
            }}
          />
        );
      default:
        return <Chip label={state} size="small" sx={{ borderRadius: RADIUS_ITEM }} />;
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const allCampaigns = campaignsData?.WhatsAppCampaigns || [];

  const selectedBUName = useMemo(() => {
    if (!selectedBusinessUnitId || selectedBusinessUnitId <= 0) return null;
    return allCampaigns.find((c) => c.businessUnitId === selectedBusinessUnitId)?.businessUnit?.name ?? null;
  }, [allCampaigns, selectedBusinessUnitId]);

  const filteredCampaigns = useMemo(() => {
    return allCampaigns.filter((c) => {
      const matchesBU =
        !selectedBusinessUnitId || selectedBusinessUnitId <= 0 || c.businessUnitId === selectedBusinessUnitId;
      const matchesName = !nameFilter || c.name.toLowerCase().includes(nameFilter.toLowerCase());
      const matchesStatus = statusFilter === 'ALL' || c.state === statusFilter;
      return matchesBU && matchesName && matchesStatus;
    });
  }, [allCampaigns, selectedBusinessUnitId, nameFilter, statusFilter]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
        <CircularProgress sx={{ color: NAVY }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography sx={{ color: '#B00020', fontFamily: 'Jost, sans-serif' }}>Error al cargar las campañas</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {selectedBusinessUnitId && selectedBusinessUnitId > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: SURFACE_CONTAINER_LOW,
                px: 1.5,
                py: 0.5,
                borderRadius: RADIUS_ITEM,
              }}
            >
              <BusinessIcon sx={{ fontSize: 16, color: NAVY, opacity: 0.6 }} />
              <Typography
                variant="caption"
                sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, fontWeight: 500, opacity: 0.7 }}
              >
                {selectedBUName ?? `UdN #${selectedBusinessUnitId}`}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.5 }}>
            {filteredCampaigns.length} campaña{filteredCampaigns.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreateCampaign}
          sx={{
            bgcolor: YELLOW,
            color: NAVY,
            fontFamily: 'Jost, sans-serif',
            fontWeight: 700,
            borderRadius: RADIUS_COIN,
            px: 3,
            boxShadow: SHADOW,
            '&:hover': { bgcolor: '#e6c009' },
          }}
        >
          Nueva Campaña
        </Button>
      </Box>

      {/* Filter bar */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'center',
          bgcolor: SURFACE_CONTAINER_LOW,
          borderRadius: RADIUS_CARD,
          p: 2,
        }}
      >
        <FilterListIcon sx={{ color: NAVY, opacity: 0.4, fontSize: 20 }} />
        <TextField
          placeholder="Buscar por nombre..."
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          size="small"
          sx={{
            flex: 1,
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              bgcolor: SURFACE_CONTAINER_LOWEST,
              borderRadius: RADIUS_ITEM,
              fontFamily: 'Jost, sans-serif',
              '& fieldset': { border: 'none' },
              boxShadow: SHADOW,
            },
            '& input': { fontFamily: 'Jost, sans-serif', color: NAVY },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: NAVY, opacity: 0.4 }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          size="small"
          sx={{
            bgcolor: SURFACE_CONTAINER_LOWEST,
            borderRadius: RADIUS_ITEM,
            fontFamily: 'Jost, sans-serif',
            color: NAVY,
            minWidth: 160,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            boxShadow: SHADOW,
          }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily: 'Jost, sans-serif', color: NAVY }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Empty state */}
      {filteredCampaigns.length === 0 && (
        <Box
          sx={{
            bgcolor: SURFACE_CONTAINER_LOW,
            borderRadius: RADIUS_CARD,
            p: 6,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, mb: 1, fontWeight: 600 }}>
            {allCampaigns.length === 0 ? 'No hay campañas creadas' : 'Sin resultados'}
          </Typography>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.5, mb: 3 }}>
            {allCampaigns.length === 0
              ? 'Comienza creando tu primera campaña de WhatsApp'
              : 'Prueba ajustando los filtros'}
          </Typography>
          {allCampaigns.length === 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateCampaign}
              sx={{
                bgcolor: YELLOW,
                color: NAVY,
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                borderRadius: RADIUS_COIN,
                px: 3,
                boxShadow: SHADOW,
                '&:hover': { bgcolor: '#e6c009' },
              }}
            >
              Crear Primera Campaña
            </Button>
          )}
        </Box>
      )}

      {/* Campaigns table */}
      {filteredCampaigns.length > 0 && (
        <Box
          sx={{
            bgcolor: SURFACE_CONTAINER_LOW,
            borderRadius: RADIUS_CARD,
            overflow: 'hidden',
          }}
        >
          {/* Table header */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '60px 1fr 140px 160px 120px 140px 1fr',
              px: 3,
              py: 1.5,
            }}
          >
            {['ID', 'Nombre', 'Estado', 'Unidad de Negocio', 'Plantilla', 'Creado', 'Acciones'].map((col) => (
              <Typography
                key={col}
                variant="caption"
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 700,
                  color: NAVY,
                  opacity: 0.45,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {col}
              </Typography>
            ))}
          </Box>

          {/* Rows */}
          {filteredCampaigns.map((campaign, idx) => (
            <Box
              key={campaign.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr 140px 160px 120px 140px 1fr',
                px: 3,
                py: 2,
                bgcolor: idx % 2 === 0 ? SURFACE_CONTAINER_LOWEST : 'transparent',
                alignItems: 'center',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.04)' },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.45, fontWeight: 500 }}
              >
                #{campaign.id}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, fontWeight: 600, pr: 1 }}>
                {campaign.name}
              </Typography>
              <Box>{getStateChip(campaign.state)}</Box>
              <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.65 }}>
                {campaign.businessUnit?.name || `UdN #${campaign.businessUnitId}`}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  color: NAVY,
                  opacity: 0.65,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  pr: 1,
                }}
              >
                {campaign.templateName || '—'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.5, fontSize: '0.75rem' }}
              >
                {formatDate(campaign.createdAt)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  onClick={() => onViewCampaign(campaign.id)}
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                    color: NAVY,
                    bgcolor: 'rgba(21, 24, 58, 0.07)',
                    borderRadius: RADIUS_ITEM,
                    px: 1.5,
                    fontSize: '0.75rem',
                    '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.12)' },
                  }}
                >
                  Detalles
                </Button>
                <Button
                  size="small"
                  onClick={() => navigateToLeadsWithCampaignFilter(campaign.name, campaign.businessUnitId)}
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                    color: NAVY,
                    bgcolor: YELLOW,
                    borderRadius: RADIUS_ITEM,
                    px: 1.5,
                    fontSize: '0.75rem',
                    '&:hover': { bgcolor: '#e6c009' },
                  }}
                >
                  Ver Leads
                </Button>
                {campaign.state === 'RUNNING' && (
                  <IconButton
                    size="small"
                    onClick={() => setCancelDialog({ open: true, campaign })}
                    sx={{
                      color: '#B00020',
                      bgcolor: 'rgba(176, 0, 32, 0.08)',
                      borderRadius: RADIUS_ITEM,
                      '&:hover': { bgcolor: 'rgba(176, 0, 32, 0.15)' },
                    }}
                  >
                    <CancelIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <AlertDialog
        openDialog={cancelDialog.open}
        setOpenDialog={(open) => {
          const isOpen = typeof open === 'boolean' ? open : open(false);
          setCancelDialog({ open: isOpen, campaign: null });
        }}
        header="Cancelar Campaña"
        msg={`¿Estás seguro de que quieres cancelar la campaña "${cancelDialog.campaign?.name}"? Esta acción no se puede deshacer.`}
        confirmMsg="Cancelar Campaña"
        cancelMsg="Mantener Campaña"
        confirmAction={handleCancelCampaign}
      />
    </Box>
  );
};

export default CampaignsList;
